import { useEffect, useState } from 'react';
import { KeyRound, Loader2, MailCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import type { CommitteePaperItem } from '@/hooks/use-committee-papers';

type TokenState = 'NONE' | 'VALID' | 'EXPIRED';

interface MaterialLinkStatus {
  registrationId: string;
  folio: string;
  registrationStatus: string;
  paymentStatus: 'PAID' | 'NOT_PAID';
  materialStatus: 'PENDING' | 'UPLOADED';
  hasMaterial: boolean;
  tokenState: TokenState;
  tokenExpiresAt: string | null;
  eligible: boolean;
  ineligibleReason: string | null;
}

interface MaterialLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paper: CommitteePaperItem | null;
}

const VALID_TOKEN_WARNING =
  'Este ponente ya tiene un enlace vigente. Al continuar, el enlace actual será invalidado y se generará uno nuevo.';

function formatExpiry(value: string | null): string {
  if (!value) return 'No aplica';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No disponible';
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City',
  }).format(date);
}

function tokenLabel(state: TokenState): string {
  if (state === 'VALID') return 'Vigente';
  if (state === 'EXPIRED') return 'Vencido';
  return 'Sin enlace';
}

function tokenBadgeClass(state: TokenState): string {
  if (state === 'VALID') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (state === 'EXPIRED') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export function MaterialLinkDialog({ open, onOpenChange, paper }: MaterialLinkDialogProps) {
  const [status, setStatus] = useState<MaterialLinkStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !paper) {
      setStatus(null);
      setError(null);
      setIsConfirming(false);
      return;
    }

    let cancelled = false;
    const fetchStatus = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await api.get<MaterialLinkStatus>(`/speaker/material-status/${paper.id}`);
        if (!cancelled) setStatus(data);
      } catch (requestError: any) {
        if (!cancelled) {
          setError(requestError?.response?.data?.message || 'No fue posible consultar el estado del enlace.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [open, paper]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isSending) onOpenChange(nextOpen);
  };

  const handleGenerateAndSend = async () => {
    if (!paper || !status?.eligible || isSending) return;
    setIsSending(true);
    setError(null);

    try {
      const { data } = await api.post<{
        message: string;
        tokenState: 'VALID';
        tokenExpiresAt: string;
      }>(`/speaker/generate-link/${paper.id}`, {});

      setStatus((current) => current
        ? {
            ...current,
            tokenState: data.tokenState,
            tokenExpiresAt: data.tokenExpiresAt,
          }
        : current);
      setIsConfirming(false);
      toast.success(data.message || 'Nuevo enlace enviado correctamente.');
    } catch (requestError: any) {
      const message = requestError?.response?.data?.message || 'No fue posible generar y enviar el enlace.';
      setError(message);
      setIsConfirming(false);
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <KeyRound className="h-5 w-5" />
              Enlace para carga de material final
            </DialogTitle>
            <DialogDescription>
              {paper ? `Folio ${paper.folio}. Esta consulta no genera ni envía enlaces automáticamente.` : ''}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex min-h-48 items-center justify-center gap-2 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              Consultando estado real...
            </div>
          ) : null}

          {!isLoading && error ? (
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>No fue posible completar la operación</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {!isLoading && status ? (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pago</p>
                  <Badge className={status.paymentStatus === 'PAID'
                    ? 'mt-1 border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'mt-1 border-red-200 bg-red-50 text-red-700'}>
                    {status.paymentStatus === 'PAID' ? 'Pagado' : 'No pagado'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Material final</p>
                  <Badge className={status.materialStatus === 'UPLOADED'
                    ? 'mt-1 border-blue-200 bg-blue-50 text-blue-700'
                    : 'mt-1 border-slate-200 bg-white text-slate-700'}>
                    {status.materialStatus === 'UPLOADED' ? 'Recibido' : 'Pendiente'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enlace</p>
                  <Badge className={`mt-1 ${tokenBadgeClass(status.tokenState)}`}>
                    {tokenLabel(status.tokenState)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Expiración efectiva</p>
                  <p className="mt-1 text-sm font-medium text-slate-800">{formatExpiry(status.tokenExpiresAt)}</p>
                </div>
              </div>

              {status.tokenState === 'VALID' && status.eligible ? (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Rotación de enlace vigente</AlertTitle>
                  <AlertDescription>{VALID_TOKEN_WARNING}</AlertDescription>
                </Alert>
              ) : null}

              {!status.eligible ? (
                <Alert className="border-slate-300 bg-slate-50">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Acción no disponible</AlertTitle>
                  <AlertDescription>{status.ineligibleReason || 'El registro no cumple los requisitos.'}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSending}>
              Cerrar
            </Button>
            <Button
              className="bg-[#1e3a5f] text-white hover:bg-[#16304f]"
              disabled={!status?.eligible || isLoading || isSending}
              onClick={() => setIsConfirming(true)}
            >
              {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailCheck className="mr-2 h-4 w-4" />}
              Generar y enviar nuevo enlace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isConfirming} onOpenChange={(nextOpen) => !isSending && setIsConfirming(nextOpen)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar generación y envío</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                Se generará un token nuevo y se enviará al correo registrado del ponente.
              </span>
              {status?.tokenState === 'VALID' ? (
                <span className="block font-semibold text-amber-800">{VALID_TOKEN_WARNING}</span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSending}
              onClick={(event) => {
                event.preventDefault();
                void handleGenerateAndSend();
              }}
              className="bg-[#1e3a5f] text-white hover:bg-[#16304f]"
            >
              {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar y enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
